import React from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import MarkerIconGray from "assets/markers/marker-icon-grey.png";
import MarkerIconBlue from "assets/markers/marker-icon-blue.png";
import MarkerIconRed from "assets/markers/marker-icon-red.png";
import MarkerIconOrange from "assets/markers/marker-icon-orange.png";
import { Marker } from "react-map-gl";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { useTranslation } from "react-i18next";
import {
  collectOrdersForStop,
  getDeliveryStatus,
  getPathwayStopCompletedAt,
  getStatusVisual,
  STOP_STATUS_PALETTE,
} from "util/stopStatusUtils";
import { getStopOrdersBreakdownLines } from "util/pathwayOrderUtils";
import { buildStopLabelMap, formatStopTypeWithLabel, resolveAllRouteStopsWithOffsets } from "util/stopLabelUtils";
import { getWarehouseReturnPackages, filterWarehouseReturnsByStatus } from "util/warehouseReturnUtils";

const styles = makeStyles((theme) => ({
  marker: {
    width: "20px",
    height: "20px",
    position: "relative",
    borderRadius: "10px",
    cursor: "pointer",
    boxSizing: "border-box",

    "& img": {
      visibility: "hidden",
      opacity: 0,
      position: "absolute",
      top: "-42px",
      left: "-2px",
    },

    "&:hover": {
      "& img": {
        visibility: "visible",
        opacity: 1,
        transition: "visibility 0s, opacity 0.1s linear",
      },
    },
  },
  markerUnfilledBlue: {
    backgroundColor: "transparent",
    border: `1px solid ${STOP_STATUS_PALETTE.blue}`,
    "&:hover": {
      backgroundColor: STOP_STATUS_PALETTE.openHover,
      borderColor: STOP_STATUS_PALETTE.openHover,
    },
  },
  markerDriver: {
    backgroundColor: "transparent",
    border: `2px solid ${STOP_STATUS_PALETTE.blue}`,
  },
}));

export const BlueTooltip = withStyles({
  tooltip: {
    backgroundColor: "#93B5F0 !important",
    color: "#000",
    padding: "12px 16px",
    borderRadius: "4px",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
    fontSize: "14px",
    maxWidth: "260px"
  },
  arrow: {
    color: "#93B5F0 !important"
  },
  popper: {
    zIndex: 9999,
    "& .MuiTooltip-tooltip": {
      backgroundColor: "#93B5F0 !important"
    }
  }
})(Tooltip);

export const Pin = ({ color }) => {
  if (color === "red") {
    return <img alt="marker" src={MarkerIconRed} />;
  }

  if (color === "blue") {
    return <img alt="marker" src={MarkerIconBlue} />;
  }

  if (color === "orange") {
    return <img alt="marker" src={MarkerIconOrange} />;
  }

  if (color === "dark-red") {
    return <img alt="marker" src={MarkerIconRed} />;
  }

  return <img alt="marker" src={MarkerIconGray} />;
};

export const CustomMarker = React.memo(({ pathway, route, color = "gray", pinColor, onClick, stopNumber, idx, status: propStatus }) => {
  const classes = styles();
  const { t } = useTranslation();

  const renderStopTooltipContent = () => {
    const deliveryTimingConfig = route?.delivery_timing_config;
    const scanningCodesEnabled =
      deliveryTimingConfig?.permissions?.scanning_codes_enabled !== false &&
      deliveryTimingConfig?.permissions?.scanning_codes_enabled !== 0 &&
      deliveryTimingConfig?.permissions?.scanning_codes_enabled !== "0";

    const stopStatus =
      propStatus ||
      getDeliveryStatus({
        stop: pathway,
        routeStarted: !!route?.start_date,
        routeCompleted: !!route?.end_date || route?.progress === "Complete",
        scanningCodesEnabled,
        route,
        pathwayIndex: idx,
      });

    const stopDisplayName = pathway.name || (stopNumber ? `${t("Stop")} ${stopNumber}` : t("Stop"));
    const stopType = formatStopTypeWithLabel(pathway, route, idx, t);
    const completedAt = getPathwayStopCompletedAt(pathway);

    const orders = collectOrdersForStop(pathway);
    const breakdownLines = getStopOrdersBreakdownLines(orders, t, { isFixStop: pathway?.id == null });

    const statusLabel = {
      skipped: t("Stop Skipped"),
      delivered: t("Completed"),
      partial: t("Partial"),
      unsuccessful: t("Unsuccessful"),
      order_removed: t("Order Removed"),
      ready: route?.start_date
        ? (pathway.route_type?.startsWith("pickup")
          ? t("Handover In Progress")
          : t("In Progress"))
        : t("Ready"),
      in_progress: pathway.route_type?.startsWith("pickup")
        ? t("Handover In Progress")
        : t("In Progress"),
      loaded: t("Loaded"),
    }[stopStatus] || String(stopStatus || "In Progress").toUpperCase();

    const allReturnPackages = getWarehouseReturnPackages(pathway);
    let displayStatusLabel = statusLabel;
    if (allReturnPackages.length > 0) {
      const returnedPackagesCount = filterWarehouseReturnsByStatus(allReturnPackages, "delivered").length;
      displayStatusLabel = `${statusLabel} (${returnedPackagesCount}/${allReturnPackages.length})`;
    }

    return (
      <div>
        <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#000" }}>
          {stopDisplayName}
        </Typography>

        {stopType && (
          <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{t("Stop Type")}:</strong> {stopType}
          </Typography>
        )}

        {pathway.skipped_at ? (
          <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{t("Skipped At")}:</strong>{" "}
            {new Date(pathway.skipped_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        ) : completedAt ? (
          <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{t("Completed")}:</strong>{" "}
            {new Date(completedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Typography>
        ) : (
          (pathway.eta || pathway.eta_display) && (
            <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
              <strong>{t("ETA")}:</strong>{" "}
              {pathway.eta_display ||
                new Date(pathway.eta).toLocaleString([], {
                  day: "2-digit",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
            </Typography>
          )
        )}

        {breakdownLines && breakdownLines.length > 0 && breakdownLines.map((line, lineIndex) => (
          <Typography key={line.kind || lineIndex} variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{line.label}:</strong> {t("orders")} {line.orders}, {t("packages")} {line.packages}
          </Typography>
        ))}

        <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
          <strong>{t("Status")}:</strong> {displayStatusLabel}
        </Typography>

        {(pathway.skipping_reason || pathway.Orders?.some((o) => o.skipping_reason)) && (
          <Typography variant="body2" style={{ color: "#000", fontSize: "12px", marginTop: "2px" }}>
            <strong>{t("Reason")}:</strong>{" "}
            {t(pathway.skipping_reason) || t(pathway.Orders?.find((o) => o.skipping_reason)?.skipping_reason)}
          </Typography>
        )}
      </div>
    );
  };

  return (
    <Marker latitude={pathway.latitude} longitude={pathway.longitude}>
      <BlueTooltip
        arrow
        placement="top"
        title={renderStopTooltipContent()}
      >
        <div
          className={classes.marker}
          onClick={onClick}
          style={{
            position: "relative",
            backgroundColor: STOP_STATUS_PALETTE[color] ?? STOP_STATUS_PALETTE.gray,
            borderRadius: (pathway && pathway.id == null) ? "4px" : "10px"
          }}
        >
          {stopNumber && (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                pointerEvents: 'none',
                zIndex: 100000,
                marginBottom: '2px',
                whiteSpace: 'nowrap'
              }}
            >
              {stopNumber}
            </div>
          )}
        </div>
      </BlueTooltip>
    </Marker>
  );
});

const Markers = React.memo(({ route, onClick, supplierLocations }) => {
  if (!route || !route.pathway) {
    return null;
  }

  const routeStarted = !!route.start_date;
  const routeCompleted = !!route.end_date || route.progress === "Complete";
  const stopLabelMap = React.useMemo(() => buildStopLabelMap(route), [route]);

  const deliveryTimingConfig = route?.delivery_timing_config;
  const scanningCodesEnabled =
    deliveryTimingConfig?.permissions?.scanning_codes_enabled !== false &&
    deliveryTimingConfig?.permissions?.scanning_codes_enabled !== 0 &&
    deliveryTimingConfig?.permissions?.scanning_codes_enabled !== "0";

  // Compute resolved coordinates for all visible stops, applying side-by-side or circular offsets if coordinates overlap.
  const resolvedStops = React.useMemo(() => {
    return resolveAllRouteStopsWithOffsets(route, supplierLocations).filter((s) => !s.isStart);
  }, [route, supplierLocations]);

  return (
    <>
      {
        resolvedStops.map(({ pathway, idx, latitude, longitude }) => {
          const status = getDeliveryStatus({
            stop: pathway,
            routeStarted,
            routeCompleted,
            scanningCodesEnabled,
            route,
            pathwayIndex: idx,
          });
          const { color } = getStatusVisual(status, "map");

          return (
            <CustomMarker
              pathway={{ ...pathway, latitude, longitude }}
              route={route}
              key={`pathway-${idx}`}
              color={color}
              pinColor={color}
              onClick={() => onClick(route.id, idx)}
              stopNumber={stopLabelMap.get(idx)}
              idx={idx}
              status={status}
            />
          );
        })
      }
    </>
  );
});

export default Markers;
